import React, { FC, useCallback, useState, useEffect } from 'react';
import NumberFormat from 'react-number-format';
import classes from '../../asset/css/subscription-detail/product-item.module.css';
import { ProductInfoViewType } from '../../types/view/product-info-view-type';
import { SwitchOffButton } from './SwitchOffButton';
import { SwitchOnButton } from './SwitchOnButton';
import { MessageContentViewType } from '../../types/view/message-content-view-type';

export const ProductItem: FC<{
  productItem: ProductInfoViewType;
  loading: boolean;
  overrideUrl?: string;
  cancelSubscriptionFunc: (lineId?: string) => void;
  updateQuantityFunc: (lineId: string, quantity: number) => void;
  setMessageModalActive: (modalActive: boolean) => void;
  setMessageContent: (messageContent: MessageContentViewType) => void;
}> = ({ productItem, loading, overrideUrl, cancelSubscriptionFunc, updateQuantityFunc, setMessageModalActive, setMessageContent }) => {
  const [currentQuantity, setCurrentQuantity] = useState(String(productItem.quantity));
  const [currentActive, setCurrentActive] = useState(productItem.active);
  const [animating, setAnimating] = useState(false);

  // このファイルで定義しているpostMessageの返答に対する処理
  useEffect(() => {
    const changeQuantityEvent = (event: MessageEvent) => {
      if (Boolean(event.data.changeQuantityData) === false) return;

      const { variantId, lineId, quantity, isFirstLimitedItem, isFirstDiscountPrice } = event.data.changeQuantityData;

      // 複数componentで同一のmessageイベントのバインドを行うため
      // 返り値に含まれるvariantIdが同じ場合のみ処理を続行する
      if (variantId !== productItem.variantId) return;

      if (!Number.isInteger(quantity) || quantity < 0 || !Boolean(lineId)) return;

      if (isFirstLimitedItem === true && isFirstDiscountPrice === true && quantity > 1) {
        setMessageContent({
          title: '数量限定商品です',
          body: 'こちらの商品は数量限定商品のため、個数を増やすことはできません。',
        });
        setMessageModalActive(true);
        setCurrentQuantity("1");
      } else {
        updateQuantityFunc(lineId, quantity);
      }
    };
    window.addEventListener('message', changeQuantityEvent);

    return () => window.removeEventListener('message', changeQuantityEvent);
  }, [updateQuantityFunc, setCurrentQuantity, setMessageModalActive, setMessageContent]);

  // 停止を行った後にDOM側の数量表示がずれるため
  useEffect(() => {
    setCurrentQuantity(String(productItem.quantity))
  }, [productItem]);

  // ＋ボタンを押した時のイベント
  const clickPlusBtnHandler = useCallback(() => {
    if (!productItem.lineId) return;

    setCurrentQuantity(String(productItem.quantity + 1));
    window.parent.postMessage({ 
      action: 'changeQuantity',
      variantId: productItem.variantId,
      lineId: productItem.lineId,
      quantity: productItem.quantity + 1,
      price: productItem.price
    }, '*');

  }, [productItem.quantity, productItem.lineId]);

  // ーボタンを押した時のイベント
  const clickMinusBtnHandler = useCallback(() => {
    if (!productItem.lineId) return;

    setCurrentQuantity(String(productItem.quantity - 1));
    setCurrentActive(productItem.quantity - 1 > 0);
    window.parent.postMessage({ 
      action: 'changeQuantity',
      variantId: productItem.variantId,
      lineId: productItem.lineId,
      quantity: productItem.quantity - 1,
      price: productItem.price
    }, '*');

  }, [productItem.lineId, productItem.quantity]);

  const quantityInputBlurHandler = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (!productItem.lineId) return;

      if (event.target.value === '') return;

      const quantity = Number(event.target.value);
      if (!Number.isInteger(quantity) || quantity < 0) return;

      setCurrentActive(quantity > 0);

      window.parent.postMessage({ 
        action: 'changeQuantity',
        variantId: productItem.variantId,
        lineId: productItem.lineId,
        quantity,
        price: productItem.price
      }, '*');
    },
    [productItem.lineId]
  );

  const quantityInputChangeHandler = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentQuantity(event.target.value);
  }, []);

  const btnClickFunc = () => {
    setCurrentQuantity(productItem.active ? '0' : '1');
    setCurrentActive(!productItem.active);
    setAnimating(true);

    // changeActiveFunc(productItem.active, productItem.variantId, productItem.planId, productItem.lineId);
  };

  const animateCompleteFunc = () => {
    setAnimating(false);
  };

  const priceBlock = (
    <span className={classes.product_info_price_current}>
      <small>割引価格</small> ￥<NumberFormat displayType="text" value={productItem.price} thousandSeparator={true} />
    </span>
  );
  //  productItem.originalPrice === productItem.price ? (
  //    <>
  //      <span className={classes.product_info_price_current}>
  //        ￥<NumberFormat displayType="text" value={productItem.price} thousandSeparator={true} />
  //      </span>
  //    </>
  //  ) : (
  //    <>
  //      <span className={classes.product_info_price_original}>
  //        ￥<NumberFormat displayType="text" value={productItem.originalPrice} thousandSeparator={true} />
  //      </span>
  //      <span className={classes.product_info_price_arrow}>→</span>
  //      <span className={classes.product_info_price_current}>
  //        ￥<NumberFormat displayType="text" value={productItem.price} thousandSeparator={true} />
  //      </span>
  //    </>
  //  );

  let btnHtml = (
    <SwitchOffButton
      animating={animating}
      loading={loading}
      btnClickFunc={btnClickFunc}
      animateCompleteFunc={animateCompleteFunc}
    />
  );
  if ((!currentActive && !animating) || (currentActive && animating)) {
    btnHtml = (
      <SwitchOnButton
        animating={animating}
        loading={loading}
        btnClickFunc={btnClickFunc}
        animateCompleteFunc={animateCompleteFunc}
      />
    );
  }

  const cancelBtnAction = () => {
    cancelSubscriptionFunc(productItem.lineId);
  };

  return (
    <li className={classes.product_item}>
      <a href={overrideUrl || productItem.url} target="_blank" className={classes.product_thumbnail}>
        <div
          style={productItem.image ? { backgroundImage: `url('${productItem.image}')` } : {}}
          className={classes.product_thumbnail_img}
        />
      </a>

      <div className={classes.product_info}>
        {/* 商品名と価格 */}
        <div className={classes.product_info_titlePrice}>
          {/* 商品名 */}
          <h4 className={classes.product_info_title}>{productItem.productTitle}</h4>
          {/* {productItem.variantTitle && (
            <div className={classes.product_info_variantTitle}>{productItem.variantTitle}</div>
          )} */}

          {/* 商品価格 */}
          <div className={classes.product_info_price}>{priceBlock}</div>
        </div>

        {/* アクションボタン */}
        <div className={classes.product_info_actions}>
          {/* 数量変更カウンター */}
          <div className={classes.product_info_actions_counter}>
            {/* {btnHtml} */}
            <button
              className={classes.product_info_actions_counter_subtract}
              disabled={!productItem.active || loading || animating}
              onClick={clickMinusBtnHandler}
            >
              -
            </button>
            <input
              type="number"
              className={classes.product_info_actions_counter_quantity}
              value={currentQuantity}
              disabled={!productItem.active || loading || animating}
              onBlur={quantityInputBlurHandler}
              onChange={quantityInputChangeHandler}
            />
            <button
              className={classes.product_info_actions_counter_add}
              disabled={!productItem.active || loading || animating}
              onClick={clickPlusBtnHandler}
            >
              +
            </button>
          </div>

          {/* 停止ボタン */}
          <button className={classes.product_info_actions_stopBtn} onClick={cancelBtnAction}>
            停止
          </button>
        </div>
      </div>
    </li>
  );
};
